import type { FC, HTMLAttributes } from "react";
import type { DialogProps as AriaDialogProps } from "react-aria-components";

import {
	ChevronSelectorVertical,
	Contrast01,
	LogOut01,
} from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	Button as AriaButton,
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Popover as AriaPopover,
} from "react-aria-components";

import { AvatarLabelGroup } from "@/components/ui/avatar/avatar-label-group";
import { useOrgSelection } from "@/context/use-org-selection";
import { OrgSwitchPanel } from "@/features/navigation/components/org-switch-panel";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useSettings } from "@/hooks/useSettings";
import { useAuth } from "@/providers/auth-provider";
import { cx } from "@/utils/cx";

/**
 * Account menu: profile, color theme, org switch (Untitled “switch account”
 * pattern), and sign out.
 */
export const NavAccountMenu = ({
	className,
	onClose,
	...dialogProps
}: AriaDialogProps & { className?: string; onClose?: () => void }) => {
	const { setIsSettingsModalOpen } = useSettings();
	const { email, logout, name, picture } = useAuth();
	const { organizations } = useOrgSelection();

	const displayName = name || email || "Account";
	const showOrgSwitch = organizations.length > 1;

	return (
		<AriaDialog
			{...dialogProps}
			className={cx(
				"w-66 rounded-xl bg-secondary_alt shadow-lg ring ring-secondary_alt outline-hidden",
				className,
			)}
		>
			<div className="flex flex-col gap-2 border-b border-secondary p-3">
				<AvatarLabelGroup
					size="md"
					src={picture || ""}
					subtitle={email || ""}
					title={displayName}
				/>
			</div>

			<div className="flex flex-col gap-0.5 py-1.5">
				<NavAccountCardMenuItem
					icon={Contrast01}
					label="Choose Color Theme"
					onClick={() => {
						setIsSettingsModalOpen(true);
						onClose?.();
					}}
				/>
			</div>

			{showOrgSwitch && (
				<div className="border-t border-secondary py-1.5">
					<OrgSwitchPanel onClose={onClose} />
				</div>
			)}

			<div className="border-t border-secondary pt-1 pb-1.5">
				<NavAccountCardMenuItem
					icon={LogOut01}
					label="Sign out"
					onClick={() => void logout()}
				/>
			</div>
		</AriaDialog>
	);
};

const NavAccountCardMenuItem = ({
	icon: Icon,
	label,
	shortcut,
	...buttonProps
}: HTMLAttributes<HTMLButtonElement> & {
	icon?: FC<{ className?: string }>;
	label: string;
	shortcut?: string;
}) => {
	return (
		<button
			{...buttonProps}
			className={cx(
				"group/item w-full cursor-pointer px-1.5 focus:outline-hidden",
				buttonProps.className,
			)}
			type="button"
		>
			<div
				className={cx(
					"flex w-full items-center justify-between gap-3 rounded-md p-2 group-hover/item:bg-primary_hover",
					"outline-focus-ring group-focus-visible/item:outline-2 group-focus-visible/item:outline-offset-2",
				)}
			>
				<div className="flex gap-2 text-sm font-semibold text-secondary group-hover/item:text-secondary_hover">
					{Icon && <Icon className="size-5 text-fg-quaternary" />} {label}
				</div>

				{shortcut && (
					<kbd className="flex rounded px-1 py-px font-body text-xs font-medium text-tertiary ring-1 ring-secondary ring-inset">
						{shortcut}
					</kbd>
				)}
			</div>
		</button>
	);
};

/**
 * Sidebar account card trigger + popover menu (dashboard NavAccountCard pattern).
 * Opens above the trigger on mobile (drawer), to the side on desktop sidebar.
 */
export const NavAccountCard = () => {
	const triggerRef = useRef<HTMLButtonElement>(null);
	const isDesktop = useBreakpoint("lg");
	const { isSettingsModalOpen } = useSettings();
	const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
	const { email, name, picture } = useAuth();

	const userInfo = useMemo(
		() => ({
			avatar: picture || "",
			email: email || "",
			name: name || email || "Account",
		}),
		[email, name, picture],
	);

	useEffect(() => {
		if (!isSettingsModalOpen) return;
		queueMicrotask(() => {
			setIsAccountMenuOpen(false);
		});
	}, [isSettingsModalOpen]);

	return (
		<AriaDialogTrigger
			isOpen={isAccountMenuOpen}
			onOpenChange={setIsAccountMenuOpen}
		>
			<AriaButton
				className="group relative flex w-full cursor-pointer items-start gap-3 rounded-xl bg-primary p-3 text-left ring-1 ring-secondary outline-focus-ring transition-colors duration-100 ease-linear ring-inset hover:bg-primary_hover focus-visible:outline-2 focus-visible:outline-offset-2"
				ref={triggerRef}
			>
				<AvatarLabelGroup
					size="md"
					src={userInfo.avatar}
					subtitle={userInfo.email}
					title={userInfo.name}
				/>

				<div className="ml-auto flex shrink-0 items-center justify-center text-fg-quaternary group-hover:text-fg-quaternary_hover">
					<ChevronSelectorVertical className="size-4" />
				</div>

				<AriaPopover
					className={({ isEntering, isExiting }) =>
						cx(
							"origin-(--trigger-anchor-point) will-change-transform",
							isEntering &&
								"duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
							isExiting &&
								"duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
						)
					}
					offset={8}
					placement={isDesktop ? "right bottom" : "top right"}
					triggerRef={triggerRef}
				>
					<NavAccountMenu onClose={() => setIsAccountMenuOpen(false)} />
				</AriaPopover>
			</AriaButton>
		</AriaDialogTrigger>
	);
};
